import {
    Component,
    Output,
    EventEmitter,
    Input,
    OnChanges,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    SimpleChanges
} from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Account } from '@app/core/resources/account';
import { AccountService } from '@app/core/services/account.service';
import { GlobalStateService } from '@app/core/services/global-state.service';
import { delay } from 'rxjs/operators';
import { DocumentCollection } from 'ngx-jsonapi';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { CreateAccountComponent } from '@app/dashboard/accounts/create-account/create-account.component';
import { Observable } from 'rxjs';
import { AccountDetailsService } from '@app/core/services/account-details.service';
import { NetworkService } from '@app/core/services/network.service';

@Component({
    selector: 'app-select-accounts',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './select-accounts.component.html',
    styleUrls: ['./select-accounts.component.scss']
})
export class SelectAccountsComponent implements OnChanges {
    public selectAccountsForm: FormGroup = new FormGroup({});
    public accounts: Array<Account> = new Array<Account>();
    public filteredAccounts: Array<Account> = new Array<Account>();
    public showSkeleton: boolean = true;
    public styleContentLoader: { [key: string]: string } = {
        width: '100%',
        height: '138px'
    };
    public pageSize: number = 250;
    public pageNumber: number = 1;
    @Input() public initialAccount: Account | undefined;
    @Input() public label: string = 'Selecciona tu cuenta';
    @Input() public filterById: string = '';
    @Input() public filterName: string = '';
    @Input() public filterByGroupId: string = '';
    @Input() public filterCanSendOrReceive: string = '';
    @Input() public account: Account | null = new Account();
    @Input() public disabled: boolean = false;
    @Output() public accountsCollection: EventEmitter<Array<Account>> = new EventEmitter<Array<Account>>();
    @Output() public selectedAccount: EventEmitter<Account> = new EventEmitter<Account>();

    public constructor(
        private globalStateService: GlobalStateService,
        private accountService: AccountService,
        private builder: FormBuilder,
        private accountDetailsService: AccountDetailsService,
        private changeDetectorRef: ChangeDetectorRef,
        public networkService: NetworkService,
        private matDialog: MatDialog
    ) {
        this.buildForm();
        this.observerAccountFormValue();
        this.accountDetailsService.register();
        this.getAccount()
            .pipe(delay(0))
            .subscribe((accounts: DocumentCollection<Account>): void => {
                this.refreshAccounts(accounts.data);
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.checkStatusSelect();
        this.filterAccountList();
        if (changes.initialAccount !== undefined) {
            this.account = this.initialAccount ? this.initialAccount : new Account();
            this.selectAccountsForm.controls.account.setValue(this.initialAccount);
            this.changeDetectorRef.markForCheck();
        }
    }

    private checkStatusSelect(): void {
        if (this.disabled) {
            this.selectAccountsForm.controls.account.disable();

            return;
        }

        this.selectAccountsForm.controls.account.enable();
    }

    public trackByAccount(_index: number, account: Account): string {
        return account.id;
    }

    private refreshAccounts(accounts: Array<Account>): void {
        this.accounts = accounts;
        this.showSkeleton = false;
        this.filterAccountList();
    }

    public filterAccountList(): void {
        this.filteredAccounts = this.accounts;
        if (this.filterById) {
            this.filteredAccounts = this.accounts.filter((account): boolean => account.relationships.system.data?.id === this.filterById);
        } else if (this.filterByGroupId) {
            this.filteredAccounts = this.accounts.filter(
                (account): boolean => account.relationships.system.data?.attributes.group_id === this.filterByGroupId
            );
        } else if (this.filterCanSendOrReceive) {
            this.filteredAccounts = this.accounts.filter(
                (account): boolean => account.relationships.system.data?.attributes['can_' + this.filterCanSendOrReceive]
            );
        }
        this.accountsCollection.emit(this.filteredAccounts);
        this.changeDetectorRef.detectChanges();
    }

    private getAccount(): Observable<DocumentCollection<Account>> {
        let userId: string | undefined = this.globalStateService.userId;

        /* eslint-disable id-blacklist */
        return this.accountService.all({
            beforepath: `users/${userId}`,
            remotefilter: { status: { ne: 'archived' } },
            include: ['system', 'account_detail', 'network'],
            sort: ['status'],
            page: { number: this.pageNumber, size: this.pageSize }
        });
        /* eslint-enable id-blacklist */
    }

    private buildForm(): void {
        this.selectAccountsForm = this.builder.group({
            account: [this.account]
        });
    }

    private observerAccountFormValue(): void {
        this.selectAccountsForm.controls.account.valueChanges.subscribe((account): void => {
            this.selectedAccount.emit(account);
        });
    }

    public openDialog(): void {
        let dialog: MatDialogRef<CreateAccountComponent> = this.matDialog.open(CreateAccountComponent, {
            width: '600px',
            data: {
                system_id: this.filterById || this.filterByGroupId
            }
        });
        dialog.afterClosed().subscribe((data): void => {
            if (data === undefined) {
                return;
            }
            this.getAccount().subscribe((accounts): void => {
                let accountValue: Account | null = accounts.find(data.account.id);
                this.account = accounts.find(data.account.id);
                this.selectAccountsForm.controls.account.setValue(accountValue);
                this.refreshAccounts(accounts.data);
            });
        });
    }
}
